import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  PettyCashService,
  PettyCashLedgerDto,
  PettyCashFilterDto,
  PettyCashPeriodSummaryDto,
  RecordPettyCashReceiptDto,
  RecordPettyCashPaymentDto,
  CancelPettyCashDto
} from '../../../services/petty-cash.service';
import { ExternalDataService } from '../../../services/externalDataService.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';

@Component({
  selector: 'app-petty-cash-list',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, PaginationComponent],
  templateUrl: './petty-cash-list.component.html',
  styleUrl: './petty-cash-list.component.css'
})
export class PettyCashListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // State properties
  ledgerEntries: PettyCashLedgerDto[] = [];
  
  periodSummary: PettyCashPeriodSummaryDto | null = null;
  loading = false;
  
  errorMessage = '';
  successMessage = '';

  // Pagination
  pageIndex = 0;
  pageSize = 10;
  totalCount = 0;

  // Selected staff balance in hand
  commonBalance: number | null = null;

  // Filter Form
  filterForm!: FormGroup;

  // Receipt Modal
  showReceiptModal = false;
  receiptForm!: FormGroup;
  submittingReceipt = false;

  // Payment Modal
  showPaymentModal = false;
  paymentForm!: FormGroup;
  submittingPayment = false;

  // Cancellation Modal
  showCancelModal = false;
  selectedEntryToCancel: PettyCashLedgerDto | null = null;
  cancelReason = '';
  cancellingEntry = false;

  // Date restriction properties
  maxPastDays = 30;
  minTransactionDate = '';
  maxTransactionDate: string = new Date().toISOString().split('T')[0];

  // Head Constants
  readonly receiptHeads = [
    { value: 'Agent Payment', label: 'Agent Payment (Ref required)' },
    { value: 'Voucher Collection', label: 'Voucher Collection' },
    { value: 'Manual', label: 'Manual' }
  ];

  readonly paymentHeads = [
    { value: 'Travel expenditure', label: 'Travel expenditure' },
    { value: 'Company Transfer', label: 'Company Transfer (Ref required)' },
    { value: 'Manual', label: 'Manual' }
  ];

  readonly transactionTypes = [
    { value: 'All', label: 'All Transactions' },
    { value: 'Receipt', label: 'Receipts Only (Payment In)' },
    { value: 'Payment', label: 'Payments Only (Payment Out)' }
  ];

  Math = Math;

  constructor(
    private fb: FormBuilder,
    private pettyCashService: PettyCashService,
    private externalDataService: ExternalDataService
  ) {
    this.initForms();
  }

  ngOnInit(): void {
    this.loadCommonBalance();
    this.loadGlobalSettings();
    this.loadLedgerEntries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadGlobalSettings(): void {
    this.externalDataService
      .getGlobalSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          if (settings && settings.maxPastDaysForLedgerEntry !== undefined && settings.maxPastDaysForLedgerEntry !== null) {
            this.maxPastDays = settings.maxPastDaysForLedgerEntry;
          }
          this.updateMinTransactionDate();
        },
        error: (err) => {
          console.error('Error loading global settings for ledger date limits:', err);
          this.updateMinTransactionDate();
        }
      });
  }

  updateMinTransactionDate(): void {
    if (this.maxPastDays > 0) {
      const minDate = new Date();
      minDate.setDate(minDate.getDate() - this.maxPastDays);
      this.minTransactionDate = minDate.toISOString().split('T')[0];
    } else {
      this.minTransactionDate = '';
    }
  }

  private initForms(): void {
    this.filterForm = this.fb.group({
      fromTransactionDate: [''],
      toTransactionDate: [''],
      entryType: [''],
      head: [''],
      transactionType: ['All']
    });

    this.receiptForm = this.fb.group({
      head: ['Agent Payment', Validators.required],
      receiptAmount: [null, [Validators.required, Validators.min(0.01)]],
      transactionDate: [new Date().toISOString().split('T')[0], Validators.required],
      referenceId: [''],
      description: ['']
    });

    this.paymentForm = this.fb.group({
      head: ['Travel expenditure', Validators.required],
      paymentAmount: [null, [Validators.required, Validators.min(0.01)]],
      transactionDate: [new Date().toISOString().split('T')[0], Validators.required],
      referenceId: [''],
      description: ['']
    });
  }

  get currentPage(): number {
    return this.pageIndex + 1;
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  loadCommonBalance(): void {
    this.pettyCashService
      .getBalance()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (bal: number) => {
          this.commonBalance = bal;
        },
        error: (err: any) => {
          console.error('Error loading petty cash balance:', err);
        }
      });
  }

  loadLedgerEntries(): void {
    this.loading = true;
    this.errorMessage = '';

    const filters = this.filterForm.value;
    const requestFilters: PettyCashFilterDto = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      fromTransactionDate: filters.fromTransactionDate || undefined,
      toTransactionDate: filters.toTransactionDate || undefined,
      entryType: filters.entryType || undefined,
      head: filters.head || undefined,
      transactionType: filters.transactionType || 'All'
    };

    this.pettyCashService
      .getLedgerEntries(requestFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.ledgerEntries = response.items || [];
          this.totalCount = response.totalCount || 0;
          this.periodSummary = response.summary || null;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading petty cash entries:', error);
          this.errorMessage = 'Failed to load petty cash ledger entries. Please check connection and try again.';
          this.loading = false;
        }
      });
  }

  applyFilters(): void {
    this.pageIndex = 0;
    this.loadLedgerEntries();
  }

  clearFilters(): void {
    this.periodSummary = null;
    this.filterForm.reset({
      fromTransactionDate: '',
      toTransactionDate: '',
      entryType: '',
      head: '',
      transactionType: 'All'
    });
    this.pageIndex = 0;
    this.loadLedgerEntries();
  }

  hasActiveFilters(): boolean {
    const v = this.filterForm.value;
    return !!(v.fromTransactionDate || v.toTransactionDate || v.entryType || v.head || v.transactionType !== 'All');
  }

  onPageChange(page: number): void {
    this.pageIndex = page - 1;
    this.loadLedgerEntries();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 0;
    this.loadLedgerEntries();
  }

  // Record Receipt Modal
  openRecordReceiptModal(): void {
    this.showReceiptModal = true;
    this.errorMessage = '';
    this.receiptForm.reset({
      head: 'Agent Payment',
      receiptAmount: null,
      transactionDate: new Date().toISOString().split('T')[0],
      referenceId: '',
      description: ''
    });
  }

  closeReceiptModal(): void {
    this.showReceiptModal = false;
  }

  isReceiptRefRequired(): boolean {
    const head = this.receiptForm.get('head')?.value;
    return head === 'Agent Payment';
  }

  submitReceipt(): void {
    if (this.receiptForm.invalid) {
      this.receiptForm.markAllAsTouched();
      return;
    }

    const val = this.receiptForm.value;
    if (this.isReceiptRefRequired() && !val.referenceId?.trim()) {
      this.errorMessage = 'Reference ID is required for Agent Payment receipts.';
      return;
    }

    if (this.minTransactionDate && val.transactionDate < this.minTransactionDate) {
      this.errorMessage = `Transaction date cannot be more than ${this.maxPastDays} days in the past (earliest allowed: ${this.minTransactionDate}).`;
      return;
    }

    this.submittingReceipt = true;
    this.errorMessage = '';

    const dto: RecordPettyCashReceiptDto = {
      head: val.head,
      receiptAmount: val.receiptAmount,
      transactionDate: new Date(val.transactionDate).toISOString(),
      referenceId: val.referenceId?.trim() || undefined,
      referenceType: val.head,
      description: val.description?.trim() || undefined
    };

    this.pettyCashService
      .recordReceipt(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.submittingReceipt = false;
          this.showReceiptModal = false;
          this.successMessage = 'Petty cash receipt recorded successfully!';
          this.loadCommonBalance();
          this.loadLedgerEntries();
          this.autoClearSuccess();
        },
        error: (err) => {
          console.error('Error recording receipt:', err);
          this.errorMessage = err?.error?.error || err?.error?.message || 'Failed to record receipt.';
          this.submittingReceipt = false;
        }
      });
  }

  // Record Payment Modal
  openRecordPaymentModal(): void {
    this.showPaymentModal = true;
    this.errorMessage = '';
    this.paymentForm.reset({
      head: 'Travel expenditure',
      paymentAmount: null,
      transactionDate: new Date().toISOString().split('T')[0],
      referenceId: '',
      description: ''
    });
  }

  closePaymentModal(): void {
    this.showPaymentModal = false;
  }

  isPaymentRefRequired(): boolean {
    const head = this.paymentForm.get('head')?.value;
    return head === 'Company Transfer';
  }

  submitPayment(): void {
    if (this.paymentForm.invalid) {
      this.paymentForm.markAllAsTouched();
      return;
    }

    const val = this.paymentForm.value;
    if (this.isPaymentRefRequired() && !val.referenceId?.trim()) {
      this.errorMessage = 'Reference ID is required when transferring to company account.';
      return;
    }

    if (this.minTransactionDate && val.transactionDate < this.minTransactionDate) {
      this.errorMessage = `Transaction date cannot be more than ${this.maxPastDays} days in the past (earliest allowed: ${this.minTransactionDate}).`;
      return;
    }

    this.submittingPayment = true;
    this.errorMessage = '';

    const dto: RecordPettyCashPaymentDto = {
      head: val.head,
      paymentAmount: val.paymentAmount,
      transactionDate: new Date(val.transactionDate).toISOString(),
      referenceId: val.referenceId?.trim() || undefined,
      referenceType: val.head,
      description: val.description?.trim() || undefined
    };

    this.pettyCashService
      .recordPayment(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.submittingPayment = false;
          this.showPaymentModal = false;
          this.successMessage = 'Petty cash payment recorded successfully!';
          this.loadCommonBalance();
          this.loadLedgerEntries();
          this.autoClearSuccess();
        },
        error: (err) => {
          console.error('Error recording payment:', err);
          this.errorMessage = err?.error?.error || err?.error?.message || 'Failed to record payment.';
          this.submittingPayment = false;
        }
      });
  }

  isAutomaticEntry(entry: PettyCashLedgerDto): boolean {
    if (entry.isAutomatic !== undefined) {
      return entry.isAutomatic;
    }
    const head = entry.head?.toLowerCase() || '';
    const refType = entry.referenceType?.toLowerCase() || '';
    const entryType = entry.entryType?.toLowerCase() || '';
    return (
      entryType === 'adjustment' ||
      entryType === 'openingbalance' ||
      head === 'agent payment' ||
      refType === 'agent payment' ||
      head === 'voucher collection' ||
      refType === 'voucher collection'
    );
  }

  // Cancel Modal
  openCancelModal(entry: PettyCashLedgerDto): void {
    this.selectedEntryToCancel = entry;
    this.cancelReason = '';
    this.showCancelModal = true;
    this.errorMessage = '';
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
    this.selectedEntryToCancel = null;
    this.cancelReason = '';
  }

  confirmCancelEntry(): void {
    if (!this.selectedEntryToCancel) return;

    this.cancellingEntry = true;
    this.errorMessage = '';

    const dto: CancelPettyCashDto = {
      ledgerEntryId: this.selectedEntryToCancel.id,
      reason: this.cancelReason?.trim() || undefined,
      transactionDate: new Date().toISOString()
    };

    this.pettyCashService
      .cancelLedgerEntry(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.cancellingEntry = false;
          this.showCancelModal = false;
          this.selectedEntryToCancel = null;
          this.cancelReason = '';
          this.successMessage = 'Petty cash entry cancelled successfully with reversal!';
          this.loadCommonBalance();
          this.loadLedgerEntries();
          this.autoClearSuccess();
        },
        error: (err) => {
          console.error('Error cancelling entry:', err);
          this.errorMessage = err?.error?.error || err?.error?.message || 'Failed to cancel entry.';
          this.cancellingEntry = false;
        }
      });
  }

  private autoClearSuccess(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatCurrency(amount?: number | null): string {
    if (amount === undefined || amount === null) return '—';
    return `₹${amount.toFixed(2)}`;
  }
}
